import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect, useMemo, useState } from 'react'

import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { GoogleTagManager } from '@next/third-parties/google'
import { NavigationHistoryProvider } from 'context/NavigationHistoryContext'
import { RegistrationProvider } from 'context/RegistrationContext'

import { OverchatEnv } from 'types/common/common.types'

import { setDatadogUser, setupDatadog } from 'modules/datadog'
import { GtmProvider } from 'modules/gtm'

import { intlLocaleToLocale, loadOverchatBasePropsPerBrand } from 'utils/common'

import CookieConsentBanner from '../components/CookieConsentBanner'
import CookieConsentFloatingButton from '../components/CookieConsentBanner/CookieConsentFloatingButton'
import CookieConsentPreferences from '../components/CookieConsentBanner/CookieConsentPreferences'
import ModalGeoContext from '../components/ModalGeoContext'
import { ContainerProvider } from '../context/ContainerContext'
import { GeoProvider } from '../context/GeoContext'
import { LocaleProvider } from '../context/LocaleContext'
import { useIsPage } from '../hooks/common/useIsPage'
import { useAuth } from '../store/useAuth'
import '../styles/global.scss'
import { getGtmId } from '../utils/getGtmId'

function App({ Component, pageProps }: AppProps) {
  const [overlabsReactLoaded, setOverlabsReactLoaded] = useState(false)
  const [overlabsReactDOMLoaded, setOverlabsReactDOMLoaded] = useState(false)
  const [overlabsMQTTLoaded, setOverlabsMQTTLoaded] = useState(false)

  const router = useRouter()

  /**
   * The error page (_error.tsx) does not reliably receive `locale` in its
   * page props, so fall back to the router's locale and finally to the
   * configured default. This keeps LocaleProvider (and every consumer, such
   * as useNavigate) from resolving to an undefined locale.
   */
  const locale =
    pageProps.locale ??
    router.locale ??
    intlLocaleToLocale(process.env.NEXT_PUBLIC_DEFAULTLOCALE)
  const brand =
    process.env.NEXT_PUBLIC_BRAND && process.env.NEXT_PUBLIC_BRAND === 'skymais'
      ? 'sky-mais'
      : 'dgo'
  const market =
    process.env.NEXT_PUBLIC_BRAND && process.env.NEXT_PUBLIC_BRAND === 'skymais'
      ? 'br'
      : 'ssla'
  const vwoEnv =
    process.env.NEXT_PUBLIC_VWO_ENV || 'https://dev.visualwebsiteoptimizer.com'
  const OVERCHAT_URL =
    process.env.NEXT_PUBLIC_OVERCHAT_URL ||
    'https://overchat.overlabs.com/overchat-widget.umd.js'
  const OVERCHAT_API_KEY = process.env.NEXT_PUBLIC_OVERCHAT_API_KEY
  const OVERCHAT_EXTERNAL_ID = process.env.NEXT_PUBLIC_OVERCHAT_EXTERNAL_ID
  const OVERCHAT_COUNTRY_SELECT =
    process.env.NEXT_PUBLIC_OVERCHAT_COUNTRY_SELECT || false
  const RECAPTCHA_V3_PUBLIC_KEY =
    process.env.NEXT_PUBLIC_RECAPTCHAV3_PUBLIC_KEY || ''

  const envMap: Record<string, OverchatEnv> = {
    development: 'hml',
    production: 'prd'
  }

  const env: OverchatEnv = 'prd'
  // envMap[process.env.NODE_ENV ?? ''] || 'prd'

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false
          }
        }
      })
  )

  const gtmId = useMemo(() => {
    return getGtmId(locale)
  }, [locale])

  useEffect(() => {
    setupDatadog()
    // Re-attach the user when returning/refreshing mid-flow with an existing
    // session already in storage (no-op for anonymous visitors).
    setDatadogUser(useAuth.getState())
  }, [])

  const shouldOverchatHaveDefaultPos = useIsPage(
    ['PLANS', 'WELCOME', 'HOME', 'OTP'],
    market
  )

  // This useEffect is meant to adjust the position of the overchat button in pages
  // that do not have a floating menu on the bottom, like the Summary.
  useEffect(() => {
    if (shouldOverchatHaveDefaultPos && window.OverchatWidget) {
      window.OverchatWidget?.setOptions({
        offsetBottom: 0
      })
    }
  }, [shouldOverchatHaveDefaultPos])

  const [shouldLoadGtm, setShouldLoadGtm] = useState(false)

  useEffect(() => {
    setShouldLoadGtm(true)
  }, [])

  return (
    <>
      {/* This is the recommended place to add scripts as per nextjs documentation */}

      {/* Remove GTM scripts in the dev server. They added something to the script that makes the server break in development. */}
      {process.env.NODE_ENV === 'production' && shouldLoadGtm && (
        <>
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height={0}
              width={0}
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
          <GoogleTagManager
            gtmId={gtmId || ''}
            gtmScriptUrl={
              locale === 'ar'
                ? `${window.location.origin}/metrics-ar/` // TCS-7949: GTG reverse proxy will work on our own domains
                : undefined
            }
          />
        </>
      )}
      {/* TCS-6884: Analytics tooling for business */}
      {/* GrowthBook only for SKY+ */}
      {process.env.NODE_ENV === 'production' && brand === 'sky-mais' && (
        <>
          <Script
            strategy="lazyOnload"
            data-api-host="https://cdn.growthbook.io"
            data-client-key="sdk-dMa8bMfBQEw2rTEu"
            src="https://cdn.jsdelivr.net/npm/@growthbook/growthbook/dist/bundles/auto.min.js"
            crossOrigin="anonymous"
          ></Script>
        </>
      )}
      {process.env.NODE_ENV === 'production' && brand === 'dgo' && (
        <>
          {/* Start VWO Async SmartCode */}
          <link rel="preconnect" href={vwoEnv} />

          <Script
            id="vwoCode"
            strategy="afterInteractive"
            crossOrigin="anonymous"
            dangerouslySetInnerHTML={{
              __html: `
            window._vwo_code = window._vwo_code || (function() {
              var account_id = 713189,
                version = 1.5,
                settings_tolerance = 2000,
                library_tolerance = 2500,
                use_existing_jquery = false,
                is_spa = 1,
                hide_element = 'body',
                hide_element_style =
                  'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                f = false,
                w = window,
                d = document,
                vwoCodeEl = d.querySelector('#vwoCode'),
                code = {
                  use_existing_jquery: function() {
                    return use_existing_jquery
                  },
                  library_tolerance: function() {
                    return library_tolerance
                  },
                  hide_element_style: function() {
                    return '{' + hide_element_style + '}'
                  },
                  finish: function() {
                    if (!f) {
                      f = true
                      var e = d.getElementById('_vis_opt_path_hides')
                      if (e) e.parentNode.removeChild(e)
                    }
                  },
                  finished: function() {
                    return f
                  },
                  load: function(e) {
                    var t = d.createElement('script')
                    t.fetchPriority = 'high'
                    t.src = e
                    t.type = 'text/javascript'
                    t.onerror = function() {
                      _vwo_code.finish()
                    }
                    d.getElementsByTagName('head')[0].appendChild(t)
                  },
                  getVersion: function() {
                    return version
                  },
                  getMatchedCookies: function(e) {
                    var t = []
                    if (document.cookie) {
                      t = document.cookie.match(e) || []
                    }
                    return t
                  },
                  getCombinationCookie: function() {
                    var e = code.getMatchedCookies(
                      /(?:^|;)\\s?(_vis_opt_exp_\\d+_combi=[^;$]*)/gi
                    )

                    e = e.map(function(e) {
                      try {
                        var t = decodeURIComponent(e)

                        if (
                          !/_vis_opt_exp_\\d+_combi=(?:\\d+,?)+\\s*$/.test(t)
                        ) {
                          return ''
                        }

                        return t
                      } catch (e) {
                        return ''
                      }
                    })

                    var i = []

                    e.forEach(function(e) {
                      var t = e.match(/([\\d,]+)/g)

                      if (t) i.push(t.join('-'))
                    })

                    return i.join('|')
                  },
                  init: function() {
                    if (d.URL.indexOf('__vwo_disable__') > -1) return

                    w.settings_timer = setTimeout(function() {
                      _vwo_code.finish()
                    }, settings_tolerance)

                    var e = d.currentScript,
                      t = d.createElement('style'),
                      i =
                        e && !e.async
                          ? hide_element
                            ? hide_element + '{' + hide_element_style + '}'
                            : ''
                          : '',
                      n = d.getElementsByTagName('head')[0]

                    t.setAttribute('id', '_vis_opt_path_hides')

                    if (vwoCodeEl)
                      t.setAttribute('nonce', vwoCodeEl.nonce)

                    t.setAttribute('type', 'text/css')

                    if (t.styleSheet) {
                      t.styleSheet.cssText = i
                    } else {
                      t.appendChild(d.createTextNode(i))
                    }

                    n.appendChild(t)

                    var o = this.getCombinationCookie()

                    this.load(
                      '${vwoEnv}/j.php?a=' +
                        account_id +
                        '&u=' +
                        encodeURIComponent(d.URL) +
                        '&f=' +
                        +is_spa +
                        '&vn=' +
                        version +
                        (o ? '&c=' + o : '')
                    )

                    return settings_timer
                  }
                }

              w._vwo_settings_timer = code.init()

              return code
            })();
          `
            }}
          />

          {/* End VWO Async SmartCode */}
        </>
      )}
      {/* TCS-6887: Maze snippet */}
      {process.env.NODE_ENV === 'production' && (
        <Script
          id="maze-script"
          strategy="afterInteractive"
          crossOrigin="anonymous"
          dangerouslySetInnerHTML={{
            __html: `
        (function (m, a, z, e) {
          var s, t;
          try {
            t = m.sessionStorage.getItem('maze-us');
          } catch (err) {}

          if (!t) {
            t = new Date().getTime();
            try {
              m.sessionStorage.setItem('maze-us', t);
            } catch (err) {}
          }

          s = a.createElement('script');
          s.src = z + '?apiKey=' + e;
          s.async = true;
          a.getElementsByTagName('head')[0].appendChild(s);
          m.mazeUniversalSnippetApiKey = e;
        })(window, document, 'https://snippet.maze.co/maze-universal-loader.js', 'cc8c4501-ec43-4a84-9eb5-7c0e7a3d0e3c');
      `
          }}
        />
      )}
      {/* TCS-6885: Overchat scripts and snippet.
          Since this should NOT block the page load, are lazily loaded.
      */}
      {process.env.NODE_ENV === 'production' && (
        <>
          <Script
            strategy="lazyOnload"
            src="https://overchat.overlabs.com/react.production.min.js"
            crossOrigin="anonymous"
            onReady={() => setOverlabsReactLoaded(true)}
          />
          {overlabsReactLoaded && (
            <Script
              strategy="lazyOnload"
              src="https://overchat.overlabs.com/react-dom.production.min.js"
              crossOrigin="anonymous"
              onReady={() => setOverlabsReactDOMLoaded(true)}
            />
          )}
          {overlabsReactDOMLoaded && (
            <Script
              strategy="lazyOnload"
              src="https://overchat.overlabs.com/mqtt.min.js"
              crossOrigin="anonymous"
              onReady={() => setOverlabsMQTTLoaded(true)}
            />
          )}
          {/* Since the execution of this script relies on window.OverchatWidget to exist,
              and since the OVERCHAT_URL script injects that method into the window,
              we should wait for this script to load
            */}
          {overlabsReactLoaded && overlabsReactDOMLoaded && overlabsMQTTLoaded && (
            <Script
              strategy="lazyOnload"
              src={`${OVERCHAT_URL}`}
              crossOrigin="anonymous"
              onReady={() => {
                if (window.OverchatWidget) {
                  const baseProps = loadOverchatBasePropsPerBrand(brand)
                  if (baseProps) {
                    console.log(env)
                    window.OverchatWidget.start({
                      ...baseProps,
                      apiKey: OVERCHAT_API_KEY || '',
                      externalId: OVERCHAT_EXTERNAL_ID || '',
                      countrySelect: OVERCHAT_COUNTRY_SELECT === 'true',
                      env: env,
                      onOpenChange: (isOpen: boolean) => {
                        window?.dataLayer?.push({
                          event: 'ga4.trackEvent',
                          eventName: 'user_interaction',
                          eventParams: {
                            action: 'overchat',
                            element: isOpen ? 'open' : 'close',
                            location: locale,
                            section: window.location.pathname
                          }
                        })
                      },
                      onStart: () => {
                        if (!sessionStorage.getItem('overChatOn')) {
                          window?.dataLayer?.push({
                            event: 'ga4.trackEvent',
                            eventName: 'user_interaction',
                            eventParams: {
                              action: 'overchat',
                              element: 'start',
                              location: locale,
                              section: window.location.pathname
                            }
                          })
                          sessionStorage.setItem('overChatOn', 'true')
                        }
                      },
                      onError: (errorMessage: string) => {
                        window?.dataLayer?.push({
                          event: 'ga4.trackEvent',
                          eventName: 'user_interaction',
                          eventParams: {
                            action: 'overchat_error',
                            element: errorMessage,
                            location: locale,
                            section: window.location.pathname
                          }
                        })
                      }
                    })
                  }
                }
              }}
            />
          )}
        </>
      )}

      {process.env.NEXT_PUBLIC_SALESFORCE_PERSONALIZATION_ENABLED ===
        'true' && (
        <Script
          id="salesforce-personalization"
          src={process.env.NEXT_PUBLIC_SALESFORCE_PERSONALIZATION_KEY}
          strategy="afterInteractive"
        />
      )}
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <div id="section-ott" className={`theme--${brand} dark`}>
            <ContainerProvider>
              <LocaleProvider locale={locale} market={market} brand={brand}>
                <GeoProvider>
                  <RegistrationProvider>
                    <NavigationHistoryProvider>
                      <GtmProvider>
                        <GoogleReCaptchaProvider
                          reCaptchaKey={RECAPTCHA_V3_PUBLIC_KEY}
                        >
                          <Component {...pageProps} />
                          <ModalGeoContext />
                          <CookieConsentBanner />
                          <CookieConsentPreferences />
                          <CookieConsentFloatingButton />
                        </GoogleReCaptchaProvider>
                      </GtmProvider>
                    </NavigationHistoryProvider>
                  </RegistrationProvider>
                </GeoProvider>
              </LocaleProvider>
            </ContainerProvider>
          </div>
        </HydrationBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  )
}
export default App
